import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import Flex from '@wix/da-ds/pkg/Flex';
import InfoIcon from '@wix/da-ds/pkg/Icons/16x16/Info';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import SophisticatedTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SophisticatedTooltip';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import StatItem from '../../../StudioDeviationList/_partials/StatItem';
import ProductListItem, { Props } from '../ProductListItem';

import s from './TierListItem.scss';

const TierListItem: FC<Props> = ({ deviation, offset, isUnlisted, ...props }) => {
  const { t } = useTranslation();
  const tier = deviation.tier;

  if (!tier) {
    return null;
  }

  const deviationsCount = tier.stats?.deviations ?? 0;
  const subscribersCount = tier.stats?.subscribers ?? 0;
  const tierPrice = tier.dollarPrice;
  const dollarPrice = tierPrice && formatDollars(parseFloat(tierPrice));
  const isPendingDeletion = tier.state === 'pending_deletion';
  const stats = (
    <Flex gap={24}>
      <StatItem title={t('studio.products.tier.stats.pricePerMonth')} value={dollarPrice} />
      <StatItem
        title={t('studio.products.tier.stats.deviationCount')}
        value={deviationsCount}
        linkTo={deviationsCount > 0 ? Url.studioTier(tier.tierId, 'deviations') : undefined}
        disabled={isUnlisted}
      />
      <StatItem
        title={t('studio.products.tier.stats.subscribersCount')}
        value={subscribersCount}
        linkTo={
          subscribersCount > 0 ? Url.customerDashboard(deviation.tier?.subproductId) : undefined
        }
        disabled={isUnlisted}
      />
      {isPendingDeletion && (
        <SophisticatedTooltip
          size="medium"
          placement="top"
          aria-label={t('studio.products.tier.pendingDeletion')}
          description={t('studio.products.tier.pendingDeletion')}
        >
          <InfoIcon
            className={s['pending-deletion-icon']}
            size={IconSize.SMALLER}
            disableDefaultColors
          />
        </SophisticatedTooltip>
      )}
    </Flex>
  );
  return <ProductListItem deviation={deviation} offset={offset} bottomSlot={stats} {...props} />;
};
TierListItem.displayName = 'TierListItem';

export default TierListItem;
